import React from 'react';
import { Select, Input } from 'antd';
import moment from 'moment';
import { connect } from 'react-redux';
import { useDebounce } from 'src/library/hooks/useDebounce';
import { searchHubs } from '../Receivables.api';
import BillingCyclePicker from './BillingCyclePicker';
import { capitalize } from 'src/utils/utils';

export interface FilterValues {
  dateRange: [moment.Moment, moment.Moment];
  entityType?: 'trip' | 'vehicle' | undefined;
  status?: string;
  hub?: string;
  hubName?: string;
  referenceNumber?: string;
  invoiceNumber?: string;
}

export type BucketFilters = {
  [key: string]: FilterValues;
};

interface FiltersProps {
  onChange: (filters: FilterValues, bucket: string) => void;
  filters: FilterValues;
  classes: Record<string, string>;
  activeTabKey: string;
  statusOptions?: {
    [key: string]: string[];
  };
  loading?: boolean;
  billingCycleType?: 'weekly' | 'monthly';
  billingCycleStart?: number;
  isVehicleLevelAggregation?: boolean;
  isCnAggregationAtTrip?: boolean;
}

const entityTypeOptions = [
  { value: '', label: 'All' },
  { label: 'Trip', value: 'trip' },
  { label: 'Vehicle', value: 'vehicle' },
];

const Filters: React.FC<FiltersProps> = ({
  onChange,
  filters,
  classes,
  activeTabKey,
  statusOptions,
  loading = false,
  billingCycleType,
  billingCycleStart,
  isVehicleLevelAggregation,
  isCnAggregationAtTrip,
}) => {
  const [localInvoiceNumber, setLocalInvoiceNumber] = React.useState<string>(filters.invoiceNumber || '');
  const [localReferenceNumber, setLocalReferenceNumber] = React.useState<string>(filters.referenceNumber || '');
  const [hubSearchQuery, setHubSearchQuery] = React.useState<string>('');
  const [hubOptions, setHubOptions] = React.useState<Array<{ label: string; value: string }>>([]);
  const [loadingHubs, setLoadingHubs] = React.useState<boolean>(false);

  const debouncedInvoiceNumber = useDebounce(localInvoiceNumber, 500);
  const debouncedReferenceNumber = useDebounce(localReferenceNumber, 500);
  const debouncedHubSearch = useDebounce(hubSearchQuery, 500);

  const getObjectTypeOptions = React.useMemo(() => {
    return entityTypeOptions.filter((option) => {
      if (isVehicleLevelAggregation) {
        return option.value !== 'trip' && option.value !== 'consignment';
      }
      if (isCnAggregationAtTrip) {
        return option.value !== 'consignment';
      }
      return true;
    });
  }, [isVehicleLevelAggregation, isCnAggregationAtTrip]);

  const handleFilterChange = <K extends keyof FilterValues>(key: K, value: FilterValues[K]) => {
    if (key === 'entityType') {
      setLocalReferenceNumber('');
      onChange({
        ...filters,
        [key]: value,
        referenceNumber: '',
      }, activeTabKey);
    } else {
      onChange({
        ...filters,
        [key]: value,
      }, activeTabKey);
    }
  };

  React.useEffect(() => {
    handleFilterChange('invoiceNumber', debouncedInvoiceNumber);
  }, [debouncedInvoiceNumber]);

  React.useEffect(() => {
    handleFilterChange('referenceNumber', debouncedReferenceNumber);
  }, [debouncedReferenceNumber]);

  React.useEffect(() => {
    setLocalInvoiceNumber(filters.invoiceNumber || '');
  }, [filters.invoiceNumber]);

  React.useEffect(() => {
    setLocalReferenceNumber(filters.referenceNumber || '');
  }, [filters.referenceNumber]);

  React.useEffect(() => {
    const fetchHubs = async () => {
      if (debouncedHubSearch?.length >= 2) {
        setLoadingHubs(true);
        try {
          const response = await searchHubs(debouncedHubSearch);
          if (response.isSuccess && response.data) {
            const formattedHubs = response.data.map((hub: any) => ({
              label: hub.hubName || hub.name || hub.hub_name,
              value: hub.hubId || hub.id || hub.hub_id,
            }));
            setHubOptions(formattedHubs);
          }
        } finally {
          setLoadingHubs(false);
        }
      } else {
        setHubOptions([]);
      }
    };

    fetchHubs();
  }, [debouncedHubSearch]);

  const currentStatusOptions = React.useMemo(() => {
    if (!statusOptions || !activeTabKey) return [];
    const options = statusOptions[activeTabKey] || [];
    return options.map((status) => ({
      label: capitalize(status),
      value: status,
    }));
  }, [statusOptions, activeTabKey]);

  return (
    <div className={classes.filterContainer}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span className={classes.filterLabel}>Billing Cycle</span>
        <BillingCyclePicker
          cycleType={billingCycleType}
          cycleStart={billingCycleStart}
          onChange={(dateRange) => handleFilterChange('dateRange', [dateRange.startDate, dateRange.endDate])}
          minWidth="240px"
          value={{ startDate: filters.dateRange[0], endDate: filters.dateRange[1] }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', width: '168px', minWidth: '168px' }}>
        <span className={classes.filterLabel}>Hub</span>
        <div className={classes.selectWrapper}>
          <Select
            className={classes.filterSelect}
            placeholder="Search Hub"
            value={filters.hub}
            showSearch
            filterOption={false}
            onSearch={(value) => setHubSearchQuery(value)}
            onChange={(value) => {
              const selectedHub = hubOptions.find((option) => option.value === value);
              onChange(
                {
                  ...filters,
                  hub: value,
                  hubName: selectedHub?.label || '',
                },
                activeTabKey,
              );
            }}
            options={hubOptions}
            loading={loadingHubs}
            allowClear
            disabled={loading}
            notFoundContent={
              hubSearchQuery.length < 2 ? 'Type at least 2 characters' : 'No hubs found'
            }
            style={{
              width: '100%',
              height: '32px',
            }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', width: '208px', minWidth: '208px' }}>
        <span className={classes.filterLabel}>Entity Type</span>
        <div className={classes.selectWrapper}>
          <Select
            className={classes.filterSelect}
            placeholder="Select"
            value={filters.entityType}
            onChange={(value) => handleFilterChange('entityType', value)}
            options={getObjectTypeOptions}
            allowClear
            disabled={loading}
            style={{
              width: '100%',
              height: '32px',
            }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', width: '280px', minWidth: '280px' }}>
        <span className={classes.filterLabel}>Reference Number</span>
        <div className={classes.selectWrapper}>
          <Input
            className={classes.input}
            placeholder="Reference Number"
            value={localReferenceNumber}
            onChange={(e) => setLocalReferenceNumber(e.target.value)}
            allowClear
            disabled={!filters.entityType || loading}
            style={{
              width: '100%',
              height: '32px',
            }}
          />
        </div>
      </div>
      {activeTabKey === 'pending' && currentStatusOptions.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', width: '168px', minWidth: '168px' }}>
          <span className={classes.filterLabel}>Status</span>
          <div className={classes.selectWrapper}>
            <Select
              className={classes.filterSelect}
              placeholder="Select"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value as string)}
              options={currentStatusOptions}
              allowClear
              disabled={loading}
              style={{
                width: '100%',
                height: '32px',
              }}
            />
          </div>
        </div>
      )}
      {activeTabKey === 'invoice_generated' && (
        <div style={{ display: 'flex', alignItems: 'center', width: '300px', minWidth: '300px' }}>
          <span className={classes.filterLabel}>Invoice Number</span>
          <div className={classes.selectWrapper}>
            <Input
              allowClear
              className={classes.input}
              placeholder="Search Invoice Number"
              value={localInvoiceNumber}
              onChange={(e) => setLocalInvoiceNumber(e.target.value)}
              disabled={loading}
              style={{
                width: '100%',
                height: '32px',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  statusOptions: state.receivables?.payoutStatusFilterOptions || {},
  loading: state.receivables?.loader || false,
  billingCycleType: state.receivables?.billingCycleType,
  billingCycleStart: state.receivables?.billingCycleStart,
  isVehicleLevelAggregation: state.receivables?.isVehicleLevelAggregation || false,
  isCnAggregationAtTrip: state.receivables?.isCnAggregationAtTrip || false,
});

export default connect(mapStateToProps)(Filters);
